import api from "@/lib/api";
import { useEffect, useState } from "react";

/**
 * R30 polish — fetch an image through the authenticated `api` instance.
 * The `Authorization: Bearer <token>` header is attached by the api
 * interceptor; the token never appears in the URL, browser history,
 * or proxy access logs. Returns a blob: URL the caller can feed to an
 * <img src=...>, plus a revoke() that MUST be called on cleanup.
 *
 * Returns null on failure so callers can hide the <img>.
 */
export async function fetchAuthedImage(path) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    return { url, revoke: () => URL.revokeObjectURL(url) };
  } catch (e) {
    return null;
  }
}

/**
 * Hook wrapper around fetchAuthedImage. Pass the URL path (relative to
 * the api baseURL, e.g. "/npd/abc/screenshot") or null to disable.
 * `deps` follows useEffect semantics. Always revokes the blob URL on
 * unmount or dep change.
 */
export function useAuthedImage(path, deps) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!path) {
      setUrl(null);
      return undefined;
    }
    let cleanup;
    let cancelled = false;
    fetchAuthedImage(path).then((res) => {
      if (cancelled) {
        if (res) res.revoke();
        return;
      }
      if (res) {
        setUrl(res.url);
        cleanup = res.revoke;
      } else {
        setUrl(null);
      }
    });
    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return url;
}
